/** Cliente UI → BFF. Resiliente a rutas aún no desplegadas (404/503). */

export type ApiError = {
  status: number;
  message: string;
  unavailable?: boolean;
};

export type ScorePayload = {
  nivel_general: 1 | 2 | 3 | 4;
  indice_IN: number;
  dimension_foco: 'EV' | 'PC' | 'OD' | 'DC' | 'GS';
  niveles: {
    EV: 1 | 2 | 3 | 4;
    PC: 1 | 2 | 3 | 4;
    OD: 1 | 2 | 3 | 4;
    DC: 1 | 2 | 3 | 4;
    GS: 1 | 2 | 3 | 4;
  };
  promedios?: {
    EV?: number;
    PC?: number;
    OD?: number;
    DC?: number;
    GS?: number;
    IN?: number;
  };
  arquetipo?: string;
  segmento?: string;
  fichas?: [string, string, string];
  fichas_niveles?: [1 | 2 | 3 | 4, 1 | 2 | 3 | 4, 1 | 2 | 3 | 4];
  recomendacion_url?: string;
  fichas_links?: Array<{
    codigo: string;
    nombre: string;
    url: string;
    nivel: 1 | 2 | 3 | 4;
  }>;
};

export type BenchmarkPayload = {
  enabled: boolean;
  n: number;
  message?: string;
  promedioPorDimension: {
    EV: number;
    PC: number;
    OD: number;
    DC: number;
    GS: number;
  } | null;
  indiceInnovacionPromedio: number | null;
};

export type SubmitResponse = {
  id: string;
  score: ScorePayload;
  benchmark?: BenchmarkPayload;
  results_token?: string;
  draft_token?: string;
};

export type ResultsResponse = {
  score: ScorePayload & {
    /** Shape crudo ScoreResult (nivel_EV…) si viene de /api/results */
    nivel_EV?: 1 | 2 | 3 | 4;
    nivel_PC?: 1 | 2 | 3 | 4;
    nivel_OD?: 1 | 2 | 3 | 4;
    nivel_DC?: 1 | 2 | 3 | 4;
    nivel_GS?: 1 | 2 | 3 | 4;
    prom_EV?: number;
    prom_PC?: number;
    prom_OD?: number;
    prom_DC?: number;
    prom_GS?: number;
    prom_IN?: number;
    fichas?: [string, string, string];
    fichas_niveles?: [1 | 2 | 3 | 4, 1 | 2 | 3 | 4, 1 | 2 | 3 | 4];
    arquetipo?: string;
    segmento?: string;
  };
  recomendaciones?: string[];
  fichas_links?: ScorePayload['fichas_links'];
  recomendacion_url?: string;
  benchmark?: BenchmarkPayload | null;
};

function unavailable(status: number): boolean {
  return status === 404 || status === 501 || status === 503;
}

async function parseJson<T>(res: Response): Promise<T | Record<string, never>> {
  try {
    return (await res.json()) as T;
  } catch {
    return {};
  }
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<{ ok: true; data: T; status: number } | { ok: false; error: ApiError }> {
  try {
    const res = await fetch(path, {
      ...init,
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...(init?.headers ?? {}),
      },
    });
    const data = await parseJson<T & { error?: string; message?: string }>(res);
    if (!res.ok) {
      return {
        ok: false,
        error: {
          status: res.status,
          message:
            data.message ??
            (data.error === 'sms_unavailable'
              ? 'SMS no disponible. Probá por email.'
              : data.error === 'rate_limit'
                ? 'Demasiados intentos. Esperá unos minutos.'
                : (data.error ?? `Error ${res.status}`)),
          unavailable: unavailable(res.status),
        },
      };
    }
    return { ok: true, data: data as T, status: res.status };
  } catch (e) {
    return {
      ok: false,
      error: {
        status: 0,
        message: (e as Error).message || 'Sin conexión con el servidor',
        unavailable: true,
      },
    };
  }
}

export function requestOtp(body: {
  email: string;
  nombre: string;
  consentimiento: true;
  channel?: 'email' | 'sms';
  phone?: string;
}): Promise<
  | {
      ok: true;
      data: { ok?: boolean; channel?: string; codeDevOnly?: string; message?: string };
      status: number;
    }
  | { ok: false; error: ApiError }
> {
  return apiFetch('/api/otp/request', { method: 'POST', body: JSON.stringify(body) });
}

export function verifyOtp(body: {
  email: string;
  code: string;
}): Promise<
  | {
      ok: true;
      data: { otp_token?: string; personal_email?: boolean; draft_token?: string };
      status: number;
    }
  | { ok: false; error: ApiError }
> {
  return apiFetch('/api/otp/verify', { method: 'POST', body: JSON.stringify(body) });
}

export function patchDraft(
  token: string,
  body: unknown,
): Promise<{ ok: true; data: unknown; status: number } | { ok: false; error: ApiError }> {
  return apiFetch(`/api/responses/${encodeURIComponent(token)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function submitSubmission(
  body: unknown,
): Promise<{ ok: true; data: SubmitResponse; status: number } | { ok: false; error: ApiError }> {
  return apiFetch<SubmitResponse>('/api/submissions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fetchResults(
  token: string,
): Promise<{ ok: true; data: ResultsResponse; status: number } | { ok: false; error: ApiError }> {
  return apiFetch<ResultsResponse>(`/api/results/${encodeURIComponent(token)}`);
}
